// custom.js — 自作台のデータモデル
//
// 自作台は正規の機種（machines.js）と同じ形をしていて、加えて
//   layout  釘・壁・仕切り・風車・入賞口の位置（board.js が組み立てる）
//   videos  液晶に流す映像の在りか（内蔵素材 か IndexedDB の Blob）
// を持つ。Game からは正規の機種と区別なく扱えるようにしてある。

import { defaultLayout, clampNailGap, inPlayfield } from './board.js';
import { getVideo } from './storage.js';
import { t } from './i18n.js';

/** 自作台の島に用意してある台数 */
export const CUSTOM_SLOTS = 6;

// まっさらな自作台の釘間隔。既定の盤面（ネオン相当）と同じ手触りから始める。
const START_NAIL_GAP = 17;

/** 液晶に流せる状態 */
export const VIDEO_STATES = [{ id: 'idle' }, { id: 'spin' }, { id: 'reach' }, { id: 'jackpot' }];
export const videoStateLabel = (id) => t(`video.state.${id}`);

/** アプリに同梱してある映像。アップロードしなくてもこれだけで組める */
export const BUILTIN_VIDEOS = [
  { id: 'wait', src: 'videos/wait.mp4' },
  { id: 'kaiten', src: 'videos/kaiten.mp4' },
  { id: 'reach', src: 'videos/reach.mp4' },
];
export const builtinName = (id) => t(`video.builtin.${id}`);

const ICONS = ['◆', '★', '☆', '◉', '７', '3', '♠', '♥', '♦', '♣', '爆', '虹', '菊', '龍'];
export const ICON_CHOICES = ICONS;

const clamp = (v, a, b) => (Number.isFinite(+v) ? Math.max(a, Math.min(b, +v)) : a);

let seq = 0;
const newId = () => `custom-${Date.now().toString(36)}-${(seq++).toString(36)}`;

/** まっさらな自作台。既定レイアウトはそのまま遊べる状態で入っている */
export function newCustomMachine(slot = 0) {
  return {
    id: newId(),
    custom: true,
    slot,
    currency: 'medal',
    name: t('custom.defaultName', { n: slot + 1 }),
    short: t('custom.defaultShort', { n: slot + 1 }),
    type: t('custom.defaultType'),
    icon: ICONS[slot % ICONS.length],
    hue: 200,
    color: '#4de0ff',
    accent: '#ffd166',
    blurb: t('custom.defaultBlurb'),
    odds: 45,
    rounds: 10,
    premiumRounds: 16,
    reachChance: 0.12,
    superReachChance: 0.35,
    roundMaxBalls: 8,
    roundTime: 1200,
    fireInterval: 28,
    nailGap: START_NAIL_GAP,
    nail: 'normal',
    awards: { start: 3, side: 2, attacker: 14 },
    layout: defaultLayout({ nailGap: START_NAIL_GAP }),
    videos: {},
    diag: null,          // 最後に走らせた試し打ち診断の結果
  };
}

/**
 * 保存されていた台を安全な範囲に均す。
 * 壊れた JSON や古い版が混ざっても、盤面が組めない値では返さない。
 */
export function sanitize(raw) {
  const base = newCustomMachine(clamp(raw?.slot, 0, CUSTOM_SLOTS - 1));
  const m = { ...base, ...raw };
  m.custom = true;
  m.currency = 'medal';
  m.id = raw?.id || base.id;
  m.slot = clamp(raw?.slot, 0, CUSTOM_SLOTS - 1);
  m.name = String(raw?.name || base.name).slice(0, 24);
  m.short = String(raw?.short || base.short).slice(0, 6);
  m.type = String(raw?.type || base.type).slice(0, 8);
  m.icon = String(raw?.icon || base.icon).slice(0, 2);
  m.blurb = String(raw?.blurb ?? base.blurb).slice(0, 60);
  m.hue = clamp(raw?.hue, 0, 359);
  m.color = colorOr(raw?.color, base.color);
  m.accent = colorOr(raw?.accent, base.accent);

  m.odds = Math.round(clamp(raw?.odds, 2, 500));
  m.rounds = Math.round(clamp(raw?.rounds, 1, 16));
  m.premiumRounds = Math.round(clamp(raw?.premiumRounds, m.rounds, 16));
  m.reachChance = clamp(raw?.reachChance, 0, 0.9);
  m.superReachChance = clamp(raw?.superReachChance, 0, 1);
  m.roundMaxBalls = Math.round(clamp(raw?.roundMaxBalls, 1, 12));
  m.roundTime = Math.round(clamp(raw?.roundTime, 300, 3000));
  m.fireInterval = Math.round(clamp(raw?.fireInterval, 12, 60));
  m.nailGap = clampNailGap(raw?.nailGap);
  m.awards = {
    start: Math.round(clamp(raw?.awards?.start, 0, 20)),
    side: Math.round(clamp(raw?.awards?.side, 0, 20)),
    attacker: Math.round(clamp(raw?.awards?.attacker, 0, 20)),
  };
  m.layout = sanitizeLayout(raw?.layout);
  m.videos = sanitizeVideos(raw?.videos);
  m.diag = raw?.diag && typeof raw.diag === 'object' ? raw.diag : null;
  return m;
}

function colorOr(v, fallback) {
  return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
}

const r1 = (v) => Math.round(v * 10) / 10;

function sanitizeLayout(raw) {
  const base = defaultLayout({ nailGap: START_NAIL_GAP });
  if (!raw || typeof raw !== 'object') return base;

  const pegs = Array.isArray(raw.pegs) ? raw.pegs : base.pegs;
  const walls = Array.isArray(raw.walls) ? raw.walls : [];
  const separators = Array.isArray(raw.separators) ? raw.separators : base.separators;
  const windmills = Array.isArray(raw.windmills) ? raw.windmills : base.windmills;

  return {
    // 盤面の外に出た釘は物理には効かないが、描画とデータを無駄に膨らませる
    pegs: pegs.slice(0, 400).map((p) => ({
      x: r1(clamp(p?.x, 0, 450)), y: r1(clamp(p?.y, 0, 800)),
      type: p?.type === 'guide' ? 'guide' : p?.type === 'sky' ? 'sky' : 'peg',
    })),
    walls: walls.slice(0, 80).map((w) => ({
      x1: r1(clamp(w?.x1, 0, 450)), y1: r1(clamp(w?.y1, 0, 800)),
      x2: r1(clamp(w?.x2, 0, 450)), y2: r1(clamp(w?.y2, 0, 800)),
    })),
    separators: separators.slice(0, 4).map((s) => ({
      x: r1(clamp(s?.x, 30, 420)),
      y1: r1(clamp(s?.y1, 110, 780)),
      y2: r1(clamp(s?.y2, 110, 790)),
      r: clamp(s?.r ?? 8, 4, 24),
    })).filter((s) => s.y2 > s.y1 + 10),
    windmills: windmills.slice(0, 6).map((w) => ({
      x: r1(clamp(w?.x, 30, 420)), y: r1(clamp(w?.y, 110, 780)),
      r: clamp(w?.r ?? 22, 10, 34),
      blades: Math.round(clamp(w?.blades ?? 4, 2, 8)),
      speed: clamp(w?.speed ?? 0.03, 0, 0.12),
      dir: w?.dir < 0 ? -1 : 1,
      phase: Number.isFinite(+w?.phase) ? +w.phase : 0,
    })),
    pockets: sanitizePockets(raw.pockets, base.pockets),
  };
}

function sanitizePockets(raw, base) {
  const out = {};
  for (const [id, pos] of Object.entries(base)) {
    const p = raw?.[id];
    const x = r1(clamp(p?.x, 40, 410));
    const y = r1(clamp(p?.y, 340, 700));
    // 範囲外の値は既定位置に戻す（clamp の下限に貼り付くと盤面が壊れて見える）
    out[id] = Number.isFinite(p?.x) && Number.isFinite(p?.y) ? { x, y } : { ...pos };
  }
  return out;
}

function sanitizeVideos(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const { id } of VIDEO_STATES) {
    const v = raw[id];
    if (!v) continue;
    const builtin = BUILTIN_VIDEOS.find((b) => b.src === v.src);
    if (v.kind === 'builtin' && builtin) {
      out[id] = { kind: 'builtin', src: v.src, builtinId: builtin.id };
    } else if (v.kind === 'blob' && typeof v.key === 'string') {
      out[id] = { kind: 'blob', key: v.key, name: String(v.name || '?'), size: +v.size || 0 };
    }
  }
  return out;
}

// ---------------------------------------------------------------- 映像

/**
 * 台の映像を再生できる形（machine.video = {状態: URL}）にして返す。
 * Blob は使う直前に URL 化する。まとめて抱えると、遊ばない台のぶんまで
 * メモリに載ってしまうため。
 */
export async function attachVideos(m, pendingFiles = null) {
  releaseVideos(m);
  const video = {};
  const urls = [];
  for (const { id } of VIDEO_STATES) {
    const v = m.videos?.[id];
    if (!v) continue;
    if (v.kind === 'builtin') { video[id] = v.src; continue; }
    // まだ保存していない差し替えは、渡されたファイルをそのまま使う
    const blob = pendingFiles?.[id] || await getVideo(v.key);
    if (!blob) continue;
    const url = URL.createObjectURL(blob);
    urls.push(url);
    video[id] = url;
  }
  m.video = Object.keys(video).length ? video : undefined;
  m._objectUrls = urls;
  return m;
}

/** attachVideos で作った URL を解放する。忘れると Blob が居座る */
export function releaseVideos(m) {
  for (const url of m?._objectUrls || []) URL.revokeObjectURL(url);
  if (m) { m._objectUrls = null; m.video = undefined; }
}

/** 保存する前に、実行時だけの持ち物を落とす */
export function toRecord(m) {
  const { video, _objectUrls, ...rest } = m;
  void video; void _objectUrls;
  return JSON.parse(JSON.stringify(rest));
}

// ---------------------------------------------------------------- 点検

/**
 * 見ただけで分かる粗を拾う。返すのは鍵で、文にするのは呼ぶ側。
 *
 * 「隙間が狭いと玉が挟まる」は確かに詰まりの主因だが、既定の盤面にも 15px の
 * 隙間はあって何万発流しても詰まらない。静的に測っただけで危険と決めつけると
 * 出るのは誤報ばかりになるので、ここでは客観的に間違っているものだけを挙げ、
 * 詰まりの判定はエディタの試し打ち診断（実際に玉を転がす）に任せる。
 */
export function inspect(layout) {
  const notes = [];
  const pegs = layout.pegs || [];
  const PEG_R = 4.5;

  if (!pegs.length) {
    notes.push({ level: 'warn', key: 'editor.check.noPegs' });
  }

  const outside = pegs.filter((p) => !inPlayfield(p.x, p.y)).length;
  if (outside) {
    notes.push({ level: 'warn', key: 'editor.check.outside', vars: { n: outside } });
  }

  // 重なった釘は 1 本と同じ働きしかせず、見た目も潰れる
  let overlap = 0;
  for (let i = 0; i < pegs.length; i++) {
    for (let j = i + 1; j < pegs.length; j++) {
      if (Math.hypot(pegs[i].x - pegs[j].x, pegs[i].y - pegs[j].y) < PEG_R * 2) overlap++;
    }
  }
  if (overlap) notes.push({ level: 'info', key: 'editor.check.overlap', vars: { n: overlap } });

  const start = layout.pockets?.start;
  if (start) {
    const near = pegs.filter((p) => Math.abs(p.x - start.x) < 40 && p.y > start.y - 90 && p.y < start.y).length;
    if (!near) notes.push({ level: 'info', key: 'editor.check.noStartPegs' });
  }

  // 仕切りが無いと左右のルートが下で合流し、弱く打ってもアタッカーに入る
  if (!(layout.separators || []).length) {
    notes.push({ level: 'info', key: 'editor.check.noSeparator' });
  }

  const atk = layout.pockets?.attacker;
  if (atk && atk.x < 250) {
    notes.push({ level: 'info', key: 'editor.check.attackerLeft' });
  }
  return notes;
}
